import TagManager from 'react-gtm-module'

// GTM Configuration
export const GTM_CONFIG = {
  gtmId: 'GTM-WX9GPFJ6'
}

// Initialize GTM
export const initGTM = () => {
  TagManager.initialize(GTM_CONFIG)
}

// Generic function to push events to dataLayer
export const pushToDataLayer = (eventData: any) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(eventData)
  }
}

// Page view events
export const trackPageView = (pagePath: string, pageTitle: string, pageLocation?: string) => {
  pushToDataLayer({
    event: 'page_view',
    page_path: pagePath + (window.location.search || ''),
    page_location: pageLocation || window.location.href,
    page_title: pageTitle
  })
}

// Personal loan page view events
export const trackPersonalPageView = (virtualPageTitle: string) => {
  const pageTitleToPathMatcher: { [key: string]: string } = {
    'Your details': 'your-details',
    'About you': 'about-you',
    'Income': 'income',
    'Financial position': 'financial-position',
    'Documents': 'doc-upload',
    'Completed application': 'end-application'
  }
  
  if (pageTitleToPathMatcher[virtualPageTitle]) {
    const virtualPageURL = window.location.origin + '/personal/' + pageTitleToPathMatcher[virtualPageTitle]
    
    // Legacy event format for backward compatibility
    pushToDataLayer({
      event: 'Pageview',
      virtualPageURL: virtualPageURL,
      virtualPageTitle: virtualPageTitle
    })
    
    // New format
    pushToDataLayer({
      event: 'page_view',
      page_path: 'personal/' + pageTitleToPathMatcher[virtualPageTitle] + (window.location.search || ''),
      page_location: virtualPageURL,
      page_title: 'Personal - ' + virtualPageTitle
    })
  }
}

// Business loan page view events
export const trackBusinessPageView = (virtualPageTitle: string) => {
  const pageTitleToPathMatcher: { [key: string]: string } = {
    'About your business': 'about-your-business',
    'About you': 'about-you',
    'Financial position': 'financial-position',
    'Application options': 'application-options',
    'Thank you': 'end-application'
  }
  
  if (pageTitleToPathMatcher[virtualPageTitle]) {
    const virtualPageURL = window.location.origin + '/business/' + pageTitleToPathMatcher[virtualPageTitle]
    
    pushToDataLayer({
      event: 'page_view',
      page_path: 'business/' + pageTitleToPathMatcher[virtualPageTitle] + (window.location.search || ''),
      page_location: virtualPageURL,
      page_title: 'Business - ' + virtualPageTitle
    })
  }
}

// Personal loan specific events
export const trackPersonalEvents = {
  // Form submission events
  submitApplication: () => pushToDataLayer({ event: 'individual_APLYiD_submit' }),
  submitCoborrowerApplication: () => pushToDataLayer({ event: 'individual_APLYiD_coborrower_submit' }),
  
  // Click events
  clickApplication: () => pushToDataLayer({ event: 'individual_APLYID_click' }),
  clickCoborrowerApplication: () => pushToDataLayer({ event: 'individual_APLYID_coborrower_click' }),
  
  // Income verification events
  clickIncomeVerification: () => pushToDataLayer({ event: 'individual_income_verification_click' }),
  clickCoborrowerIncomeVerification: () => pushToDataLayer({ event: 'individual_income_verification_coborrower_click' }),
  
  // Document upload events
  uploadUsed: () => pushToDataLayer({ event: 'individual_upload_used' }),
  uploadErrorSize: () => pushToDataLayer({ event: 'individual_upload_error_size' }),
  uploadErrorType: () => pushToDataLayer({ event: 'individual_upload_error_type' }),
  uploadErrorJpeg: () => pushToDataLayer({ event: 'individual_upload_error_jpeg' }),
  uploadErrorLength: () => pushToDataLayer({ event: 'individual_upload_error_length' }),
  
  // Callback events
  callbackRequested: () => pushToDataLayer({ event: 'individual_callback_requested' })
}

// Business loan specific events
export const trackBusinessEvents = {
  // Form submission events
  submitApplication: () => pushToDataLayer({ event: 'business_APLYiD_submit' }),
  
  // Click events
  clickApplication: () => pushToDataLayer({ event: 'business_APLYiD_click' }),
  
  // Income verification events
  clickIncomeVerification: () => pushToDataLayer({ event: 'business_income_verification_click' }),
  
  // Document upload events
  uploadUsed: () => pushToDataLayer({ event: 'business_upload_used' }),
  uploadErrorSize: () => pushToDataLayer({ event: 'business_upload_error_size' }),
  uploadErrorType: () => pushToDataLayer({ event: 'business_upload_error_type' }),
  uploadErrorJpeg: () => pushToDataLayer({ event: 'business_upload_error_jpeg' }),
  uploadErrorLength: () => pushToDataLayer({ event: 'business_upload_error_length' })
}

// General application events
export const trackGeneralEvents = {
  // Application restart event
  applicationRestart: () => pushToDataLayer({ event: 'ApplicationRestart' })
}

// Declare global dataLayer for TypeScript
declare global {
  interface Window {
    dataLayer: any[]
  }
} 